import * as React from 'react';
import { observer } from 'mobx-react';

import { UitkLink } from 'uitk-react-link';
import { UitkErrorSummary } from 'uitk-react-error-summary';

import { useStores } from 'utility/hooks';
import { TravelerInputs, TravelerAgeInputs, TravelerInfantOptions } from '.';
import { FlightsTravelerType } from '__generated__/api/types';
import { FlightsSearchFormTravelersOption } from 'types/search/wizard';
import { focusElement } from 'utility/focus-element';
import { AnchorTracked } from 'components/shared';

interface BaseTravelerOptionsProps {
  travelerOptions: FlightsSearchFormTravelersOption;
}

export const BaseTravelerOptions = observer(({ travelerOptions }: BaseTravelerOptionsProps) => {
  const { analytics, searchFormStore } = useStores();
  const ageRulesLink = travelerOptions.ageRulesLink;
  const ageRulesLinkUrl = ageRulesLink && ageRulesLink.url;
  const ageRulesDisplayAction = ageRulesLink && ageRulesLink.displayAction;
  const errorSummaryRef = React.useRef(null);
  const currentErrorString =
    travelerOptions.currentErrors && travelerOptions.currentErrors.length > 0 && travelerOptions.currentErrors[0];
  const { changeAnalytics } = travelerOptions;

  if (searchFormStore.focusErrorSummaryInTravelers) {
    setTimeout(() => {
      focusElement(errorSummaryRef);
    }, 100);
    searchFormStore.focusErrorSummaryInTravelers = false;
  }

  return (
    <>
      {currentErrorString && (
        <div className="all-b-margin-four">
          <UitkErrorSummary heading={currentErrorString} domRef={errorSummaryRef} />
        </div>
      )}
      {travelerOptions.items.map(travelerOptionItem => {
        const { type, travelerCount } = travelerOptionItem;
        const dataTestId = `${type.split('_')[0].toLowerCase()}-count`;

        return (
          <div data-test-id={dataTestId} key={`search-form-travelers-${type}`}>
            <TravelerInputs
              travelerOptionItem={travelerOptionItem}
              analytics={analytics}
              changeAnalytics={changeAnalytics}
            />

            {travelerCount > 0 && type === FlightsTravelerType.CHILD && (
              <TravelerAgeInputs travelerOptionItem={travelerOptionItem} />
            )}

            {travelerCount > 0 &&
              (type === FlightsTravelerType.INFANT_IN_SEAT || type === FlightsTravelerType.INFANT_IN_LAP) && (
                <>
                  <TravelerAgeInputs travelerOptionItem={travelerOptionItem} />
                  <TravelerInfantOptions
                    searchOptionTypeName={travelerOptions.__typename}
                    travelerOptionItem={travelerOptionItem}
                  />
                </>
              )}
          </div>
        );
      })}
      {ageRulesLinkUrl && ageRulesDisplayAction && (
        <UitkLink className="uitk-type-200 all-y-margin-three" data-test-id="airline-age-rules">
          <AnchorTracked
            href={travelerOptions.ageRulesLink.url}
            target="_blank"
            linkName={ageRulesLink.analytics.linkName}
            rfrr={ageRulesLink.analytics.rfrr}
          >
            {travelerOptions.ageRulesLink.displayAction}
          </AnchorTracked>
        </UitkLink>
      )}
    </>
  );
});
